import { useRouter } from 'next/router';
import Link from 'next/link';
import cn from 'classnames';
import { useRef } from 'react';
import { isMobile } from 'react-device-detect';
// Hooks
import {
  useGetStreams,
  useHorizontalScrollToTab,
  useStoreFeaturedParticipation,
} from 'hooks';
// Types
import { GetStoreBySlug_store } from 'api/store/types/GetStoreBySlug';
import { StreamStatus } from 'api/graphql-global-types';
// Components
import Tab from './Tab/Tab';
import SuggestedCreators from './SuggestedCreators/SuggestedCreators';
// Styles
import styles from './TabsNavigation.module.scss';

export type TabOptions = {
  name: string;
  url: string;
};

type ExtendedTabOptions = TabOptions & {
  path: string;
};

type TabsNavigationProps = {
  store: GetStoreBySlug_store;
  isAthletePending?: boolean;
  hasActiveLiveStream?: boolean;
};

const TabsNavigation = ({
  store,
  isAthletePending,
  hasActiveLiveStream = false,
}: TabsNavigationProps) => {
  const { asPath, query, pathname } = useRouter();
  const { athleteSlug } = query;

  // Refs for scrollable containers
  const shopTabsRef = useRef<HTMLDivElement>(null);
  const mediaTabsRef = useRef<HTMLDivElement>(null);
  const mainTabsRef = useRef<HTMLDivElement>(null);

  // split the page address to get current address.
  const pageAddress =
    pathname.split('/').pop() === '[athleteSlug]'
      ? ''
      : pathname.split('/').pop() || '';

  const baseProfilePath = '/' + athleteSlug;

  const shopTabList: ExtendedTabOptions[] = [];

  if (store.hasMerch) {
    shopTabList.push({
      name: 'Merch',
      path: 'merch',
      url: baseProfilePath + '/merch',
    });
  }

  // "Products" no longer has its own tab — its items now show alongside
  // memorabilia here.
  if (store.hasMemorabilia || store.hasPureProducts) {
    shopTabList.push({
      name: 'Memorabilia',
      path: 'memorabilia',
      url: baseProfilePath + '/memorabilia',
    });
  }

  if (store.hasExperiences) {
    shopTabList.push({
      name: 'experiences',
      path: 'experiences',
      url: baseProfilePath + '/experiences',
    });
  }

  const mediaTabList: ExtendedTabOptions[] = [];

  if (store.hasMediaPost) {
    mediaTabList.push({
      name: 'All',
      path: 'media',
      url: baseProfilePath + '/media',
    });

    mediaTabList.push({
      name: 'Video',
      path: 'media',
      url: baseProfilePath + '/media?type=video',
    });

    mediaTabList.push({
      name: 'Image',
      path: 'media',
      url: baseProfilePath + '/media?type=image',
    });
  }

  const articleTabList: ExtendedTabOptions[] = [];

  if (store.hasArticle) {
    articleTabList.push({
      name: 'news',
      path: 'news',
      url: baseProfilePath + '/news',
    });
  }

  // using pageAddress to detect if selected option is home, shop or channel
  const isHome = pageAddress === '';

  const isShop =
    !!shopTabList.find((tab) => tab.path === pageAddress) ||
    pageAddress === 'merch'; // in case of an empty page

  const isMedia =
    !!mediaTabList.find((tab) => tab.path === pageAddress) ||
    pageAddress === '[mediaPostSlug]' ||
    pageAddress === 'media'; // in case of an empty page

  const isContent = 'streams' === pageAddress || pageAddress === '[streamSlug]';
  const isArticle = 'news' === pageAddress || pageAddress === '[newsSlug]';

  // Get URL params to determine which tab is selected
  const { type } = query;

  const hasShopTab =
    store.hasMerch ||
    store.hasMemorabilia ||
    store.hasPureProducts ||
    store.hasExperiences;

  const hasChannelTab = store.hasStreams || store.hasActiveStreams;

  type TabState = {
    isVisible: boolean;
    isSelected: boolean;
  };

  const mainTabs: TabState[] = [
    {
      isVisible: true,
      isSelected: isHome,
    },
    {
      isVisible: hasChannelTab,
      isSelected: hasChannelTab && isContent,
    },
    {
      isVisible: store.hasMediaPost,
      isSelected: store.hasMediaPost && isMedia,
    },
    {
      isVisible: hasShopTab,
      isSelected: hasShopTab && isShop,
    },
    {
      isVisible: store.hasArticle,
      isSelected: store.hasArticle && isArticle,
    },
  ];

  // Calculate selected tab index (only counting visible tabs)
  const visibleTabs = mainTabs.filter((tab) => tab.isVisible);
  const selectedMainTabIndex = visibleTabs.findIndex((tab) => tab.isSelected);
  const selectedShopTabIndex = shopTabList.findIndex(
    (tab) => tab.path === pageAddress
  );
  const selectedMediaTabIndex = type
    ? mediaTabList.findIndex((tab) => tab.url.includes(`type=${type}`))
    : mediaTabList.findIndex((tab) => tab.name === 'All');

  // Effect to scroll to selected tab if needed for mobile
  useHorizontalScrollToTab({
    containerRef: shopTabsRef,
    selectedIndex: selectedShopTabIndex,
    shouldScroll: isMobile && isShop,
  });

  useHorizontalScrollToTab({
    containerRef: mediaTabsRef,
    selectedIndex: selectedMediaTabIndex,
    shouldScroll: isMobile && isMedia,
  });

  useHorizontalScrollToTab({
    containerRef: mainTabsRef,
    selectedIndex: selectedMainTabIndex,
    shouldScroll: isMobile,
  });

  const skipGetStreams =
    !store.hasActiveStreams || isShop || pageAddress === 'media';

  const { data: streamsData } = useGetStreams({
    variables: {
      input: {
        storeId: store?.id,
        streamStatus: [
          StreamStatus.Active,
          StreamStatus.Paused,
          StreamStatus.Interrupted,
          StreamStatus.Scheduled,
          StreamStatus.Ended,
        ],
      },
      withSponsors: true,
    },
    skip: skipGetStreams,
  });

  const activeStream = streamsData?.streamsV2.entities?.find(
    (stream) => stream.streamStatus === StreamStatus.Active
  );

  // Check if store is participant or host in featured streams
  const {
    isStoreHostInFeaturedStreams,
    isStoreParticipantInFeaturedStreams,
  } = useStoreFeaturedParticipation({ store });
  const isStoreInFeaturedStreams =
    isStoreHostInFeaturedStreams || isStoreParticipantInFeaturedStreams;

  const hasShop = !!shopTabList.length;
  const hasContent = hasChannelTab;
  const hasMediaPost = store.hasMediaPost;
  const hasNews = store.hasArticle;
  const hasHome =
    hasShop ||
    hasContent ||
    store.hasAma ||
    hasMediaPost ||
    hasNews ||
    hasActiveLiveStream;

  const shopUrl = hasShop ? shopTabList[0].url : baseProfilePath + '/merch'; // second option is for empty shop, it will display the empty page component
  const channelUrl = baseProfilePath + '/streams'; // second option is for empty channel, it will display the empty page component
  const mediaUrl = baseProfilePath + '/media';
  const newsUrl = baseProfilePath + '/news';

  const isChannel = pageAddress === 'streams';

  const isEmptyHome = baseProfilePath === asPath && !hasHome;
  const isEmptyShop = pageAddress === 'merch' && !hasShop;
  const isEmptyMedia = pageAddress === 'media' && !hasMediaPost;
  // Don't show empty page if store is in featured streams (they have streams to show)
  const isEmptyChannel = isChannel && !hasContent && !isStoreInFeaturedStreams;

  const showEmptyPage =
    isEmptyHome || isEmptyShop || isEmptyChannel || isEmptyMedia; // flag that indicates if there is an empty component or not

  const showSeparator = activeStream || isShop || showEmptyPage; // this is the line that separates base options from suboptions and empty component

  const renderEmptyPage = () => {
    if (isStoreInFeaturedStreams) {
      return null;
    }

    return (
      <div className={styles.emptyPageWrapper}>
        <SuggestedCreators />
      </div>
    );
  };

  if (isAthletePending) {
    return (
      <>
        <div className={styles.navGroup}>
          <div
            className={cn(styles.navOption, {
              [styles.highlightedNavOption]: isHome,
            })}
          >
            <Link href={baseProfilePath}>Home</Link>
          </div>
        </div>
        {isEmptyHome && renderEmptyPage()}
      </>
    );
  }

  return (
    <>
      <div className={styles.navGroup} ref={mainTabsRef}>
        <Link href={baseProfilePath} passHref>
          <a
            className={cn(styles.navOption, {
              [styles.highlightedNavOption]: isHome,
            })}
          >
            Home
          </a>
        </Link>
        {hasChannelTab && (
          <Link href={channelUrl} passHref>
            <a
              className={cn(styles.navOption, {
                [styles.highlightedNavOption]: isContent,
              })}
            >
              Channel
            </a>
          </Link>
        )}
        {store.hasMediaPost && (
          <Link href={mediaUrl} passHref>
            <a
              className={cn(styles.navOption, {
                [styles.highlightedNavOption]: isMedia,
              })}
            >
              Media
            </a>
          </Link>
        )}
        {hasShopTab && (
          <Link href={shopUrl} passHref>
            <a
              className={cn(styles.navOption, {
                [styles.highlightedNavOption]: isShop,
              })}
            >
              Shop
            </a>
          </Link>
        )}
        {store.hasArticle && (
          <Link href={newsUrl} passHref>
            <a
              className={cn(styles.navOption, {
                [styles.highlightedNavOption]: isArticle,
              })}
            >
              Articles
            </a>
          </Link>
        )}
      </div>
      <div className={cn({ [styles.navSeparator]: showSeparator })}></div>
      <div className={styles.tabsSection}>
        <div
          ref={shopTabsRef}
          className={cn(styles.hidden, {
            [styles.showTabs]: isShop && hasShop,
            [styles.allowOverflow]: isShop,
          })}
        >
          {shopTabList.map(({ name, url }) => (
            <Tab key={name} name={name} url={url} pageAddress={pageAddress} />
          ))}
        </div>
        <div
          ref={mediaTabsRef}
          className={cn(styles.hidden, {
            [styles.showTabs]: isMedia && hasMediaPost,
            [styles.allowOverflow]: isMedia,
          })}
        >
          {mediaTabList.map(({ name, url }) => (
            <Tab key={name} name={name} url={url} pageAddress={pageAddress} />
          ))}
        </div>
      </div>

      {showEmptyPage && renderEmptyPage()}
    </>
  );
};

export default TabsNavigation;
